"use client";

import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from '@/utils/supabase/client';
import { useQuery } from "@tanstack/react-query";
import { showError } from "@/utils/toast";

interface TaskAssigneeSelectProps {
  value: string;
  onChange: (value: string) => void;
}

export const TaskAssigneeSelect: React.FC<TaskAssigneeSelectProps> = ({ value, onChange }) => {
  const { profile } = useAuth();

  const { data: assignees, isLoading } = useQuery({
    queryKey: ['assignees', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) {
        console.warn('TaskAssigneeSelect: No company ID - skipping assignees query');
        return [];
      }
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .eq("company_id", profile.company_id)
        .neq("role", 'client') // Exclude clients from assignee list
        .order("first_name");
      
      if (error) {
        if (error.message.includes('permission')) {
          showError('Access denied. Contact your admin.');
          return []; // Fallback data
        }
        throw error; // Re-throw for retry
      }
      return data || [];
    },
    enabled: !!profile?.company_id, // Guard: Only query if company exists
    retry: (failureCount, error) => {
      if (error.message.includes('permission')) return false; // Don't retry permission errors
      return failureCount < 3;
    },
  });

  if (isLoading) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="Loading assignees..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="">Loading...</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder="Assign to..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="">Unassigned</SelectItem>
        {assignees?.map((assignee) => (
          <SelectItem key={assignee.id} value={assignee.id}>
            {assignee.first_name} {assignee.last_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};